/**
 * Types and how long they keep things.
 *
 * The built-in defaults were chosen for reasons — a receipt needs six years for
 * HMRC, a whiteboard needs long enough to write up — so each shows its rationale
 * beside the control. Someone overriding a default should be able to see what they
 * are overriding rather than discovering it later.
 */

import { el, mount } from './ui.ts';
import {
  listTypes, setLifetime, setLabel, addType, removeType, resetTypes,
  consequenceOf, fromLifetime, toDuration, PROTECTED_ID, type TypeDef,
} from './presets.ts';
import type { Lifetime } from '@mayfly/core';
import { showDrawer } from './views.ts';
import { storageStatus, requestPersistence, formatBytes } from './storage-status.ts';
import { consent, setConsent, type Consent } from './analytics.ts';

export async function showSettings(): Promise<void> {
  const types = await listTypes();
  const storage = await storageStatus();
  const analytics = await consent();

  mount(
    el('header', { class: 'bar' }, [
      el('button', { class: 'ghost', text: 'Back', onclick: () => void showDrawer() }),
      el('h1', { class: 'brand', text: 'Types' }),
    ]),
    el('p', { class: 'hint pad', text: 'How long each type keeps a photo. Change any of them, or add your own.' }),
    el('ul', { class: 'types' }, types.map(typeRow)),
    addRow(),
    el('div', { class: 'actions pad' }, [
      el('button', {
        class: 'ghost', text: 'Reset to defaults',
        onclick: async () => { await resetTypes(); await showSettings(); },
      }),
    ]),
    storageSection(storage),
    analyticsSection(analytics),
  );
}

/**
 * Where the photos are, in plain words. People are being asked to trust this with
 * documents, so the answer to "where does this actually go" should be in the app
 * rather than only in a README.
 */
function storageSection(storage: Awaited<ReturnType<typeof storageStatus>>): HTMLElement {
  const rows: Node[] = [
    el('h2', { text: 'Where your photos are' }),
    el('p', { class: 'hint', text:
      'In this browser, on this device, encrypted. Your photos are never uploaded — there is no '
      + 'account and no cloud copy — and they are not in your camera roll unless you saved one '
      + 'there yourself.' }),
    el('dl', { class: 'facts' }, [
      el('dt', { text: 'Kept safe from browser clean-ups' }),
      el('dd', {
        class: storage.persisted ? 'yes' : 'no',
        text: storage.persisted ? 'Yes' : 'No — the browser may clear them',
      }),
      el('dt', { text: 'Space used' }),
      el('dd', { text: `${formatBytes(storage.usage)} of ${formatBytes(storage.quota)}` }),
    ]),
  ];

  if (!storage.persisted) {
    rows.push(
      el('p', { class: 'hint', text:
        'In a private window they are deleted when you close it. Otherwise the browser may clear '
        + 'them if it needs space. Installing Mayfly to your home screen usually gets permission '
        + 'granted.' }),
      el('button', {
        class: 'ghost', text: 'Ask the browser to keep them',
        onclick: async () => { await requestPersistence(); await showSettings(); },
      }),
    );
  }
  return el('section', { class: 'storage' }, rows);
}

/** Consent, changeable at any time — it is worth nothing if it cannot be withdrawn. */
function analyticsSection(state: Consent): HTMLElement {
  const on = state === 'granted';
  return el('section', { class: 'storage', 'data-analytics': state }, [
    el('h2', { text: 'Counting visits' }),
    el('p', { class: 'hint', text:
      'Anonymous usage statistics through Google Analytics: which pages get opened and what kind '
      + 'of device. Never your photos, their labels or their types.' }),
    // The one thing people would be surprised by, so it is stated rather than
    // buried: off means no cookie and no identifier, not no contact at all.
    el('p', { class: 'hint', text:
      'Google is contacted on every visit either way. With this off it is told to store nothing, '
      + 'so no cookie is set and no identifier follows you between visits.' }),
    el('dl', { class: 'facts' }, [
      el('dt', { text: 'Currently' }),
      el('dd', {
        class: on ? 'no' : 'yes',
        text: on ? 'On' : state === 'denied' ? 'Off' : 'Not answered',
      }),
    ]),
    el('button', {
      class: 'ghost',
      text: on ? 'Turn counting off' : 'Turn counting on',
      onclick: async () => { await setConsent(on ? 'denied' : 'granted'); await showSettings(); },
    }),
    on
      // Honest about what switching off can and cannot do: a script already
      // running cannot be unloaded, so the change lands on the next visit.
      ? el('p', { class: 'hint', text:
          'Switching this off clears the cookies it set and tells the tag to store nothing from '
          + 'here on, this session included. It cannot retract a measurement already sent.' })
      : el('p', { class: 'hint', text:
          'The tag is loaded but told to store nothing, so it sets no cookie and keeps no '
          + 'identifier.' }),
  ]);
}

function typeRow(type: TypeDef): HTMLElement {
  const { value, unit } = fromLifetime(type.lifetime);
  const locked = type.id === PROTECTED_ID;

  const row = el('li', { class: locked ? 'type locked' : 'type', 'data-type': type.id }, [
    el('div', { class: 'type-head' }, [
      el('input', {
        class: 'type-label', type: 'text', value: type.label, maxlength: 32,
        'aria-label': `Name for ${type.label}`, disabled: locked,
        onchange: async (e: Event) => {
          await setLabel(type.id, (e.target as HTMLInputElement).value);
          await showSettings();
        },
      }),
      locked
        ? el('span', { class: 'lock', text: 'no expiry' })
        : el('button', {
            class: 'ghost small danger', text: type.builtIn ? 'Hide' : 'Delete',
            onclick: async () => { await removeType(type.id); await showSettings(); },
          }),
    ]),
  ]);

  if (!locked) {
    const num = el('input', {
      class: 'type-num', type: 'number', min: 1, max: 3650,
      value: unit === 'end-of-day' ? 1 : value || 1,
      'aria-label': `How long ${type.label} keeps a photo`,
      disabled: unit === 'end-of-day',
    });
    const sel = el('select', { class: 'type-unit', 'aria-label': `Unit for ${type.label}` }, [
      el('option', { value: 'hours', text: 'hours', selected: unit === 'hours' }),
      el('option', { value: 'days', text: 'days', selected: unit === 'days' }),
      el('option', { value: 'end-of-day', text: 'until end of day', selected: unit === 'end-of-day' }),
    ]);

    const apply = async () => {
      const chosen = sel.value as 'hours' | 'days' | 'end-of-day';
      const lifetime: Lifetime = chosen === 'end-of-day'
        ? { kind: 'end-of-day' }
        : toDuration(Number(num.value) || 1, chosen);
      await setLifetime(type.id, lifetime);
      await showSettings();
    };
    num.addEventListener('change', () => void apply());
    sel.addEventListener('change', () => void apply());

    row.append(el('div', { class: 'type-controls' }, [num, sel]));
  }

  row.append(el('p', { class: 'type-consequence', text: consequenceOf(type) }));
  // The reason a default is what it is, kept visible rather than lost on override.
  if (type.rationale) row.append(el('p', { class: 'type-why', text: type.rationale }));
  return row;
}

function addRow(): HTMLElement {
  const name = el('input', { class: 'search', type: 'text', placeholder: 'New type, e.g. Meter reading', maxlength: 32, 'aria-label': 'New type name' });
  const num = el('input', { class: 'type-num', type: 'number', min: 1, max: 3650, value: 7, 'aria-label': 'How long the new type keeps a photo' });
  const sel = el('select', { class: 'type-unit', 'aria-label': 'Unit for the new type' }, [
    el('option', { value: 'hours', text: 'hours' }),
    el('option', { value: 'days', text: 'days', selected: true }),
    el('option', { value: 'end-of-day', text: 'until end of day' }),
  ]);

  return el('div', { class: 'add-type' }, [
    el('h2', { text: 'Add a type' }),
    name,
    el('div', { class: 'type-controls' }, [
      num, sel,
      el('button', {
        class: 'primary', text: 'Add',
        onclick: async () => {
          const chosen = sel.value as 'hours' | 'days' | 'end-of-day';
          const lifetime: Lifetime = chosen === 'end-of-day'
            ? { kind: 'end-of-day' }
            : toDuration(Number(num.value) || 1, chosen);
          if (await addType(name.value, lifetime)) await showSettings();
        },
      }),
    ]),
  ]);
}
